// Spanish keyword overlay for @emoji-mart. The library bundles only English
// keywords in @emoji-mart/data; this map adds Spanish search terms for the
// most common emojis so searches like "risa" / "fuego" / "corazon" match.
// English keywords keep working — we append, never replace.

type EmojiData = {
  emojis: Record<string, { id: string; name?: string; keywords?: string[] }>;
};

// Keyed by emoji id (same id @emoji-mart uses internally).
// Keep values lowercase, no accents — emoji-mart normalizes input the same way.
const KEYWORDS_ES: Record<string, string[]> = {
  // ── Smileys & Emotion ──
  grinning: ["sonrisa", "feliz", "alegre", "sonriente"],
  smiley: ["sonrisa", "feliz", "alegre", "boca abierta"],
  smile: ["sonrisa", "feliz", "ojos cerrados", "risa"],
  grin: ["sonrisa", "feliz", "alegre", "diente"],
  laughing: ["risa", "carcajada", "reir", "ja ja"],
  satisfied: ["satisfecho", "risa", "feliz"],
  sweat_smile: ["sonrisa", "sudor", "alivio", "risa"],
  rofl: ["risa", "carcajada", "rodando", "muerto de risa"],
  joy: ["risa", "llorar de risa", "lagrimas", "carcajada"],
  slightly_smiling_face: ["sonrisa leve", "feliz", "amable"],
  upside_down_face: ["cara al reves", "tonto", "ironia"],
  wink: ["guino", "guinar", "complice"],
  blush: ["sonrojo", "tierno", "verguenza", "feliz"],
  innocent: ["inocente", "santo", "angel"],
  smiling_face_with_3_hearts: ["amor", "enamorado", "corazones", "carino"],
  heart_eyes: ["enamorado", "amor", "ojos corazon"],
  star_struck: ["asombro", "estrella", "fan", "ojos de estrella"],
  kissing_heart: ["beso", "corazon", "amor"],
  kissing: ["beso", "besar"],
  kissing_smiling_eyes: ["beso", "feliz"],
  kissing_closed_eyes: ["beso", "ojos cerrados"],
  yum: ["delicioso", "rico", "sabroso", "lengua"],
  stuck_out_tongue: ["lengua afuera", "burla", "broma"],
  stuck_out_tongue_winking_eye: ["lengua", "guino", "burla"],
  zany_face: ["loco", "burla", "lengua"],
  stuck_out_tongue_closed_eyes: ["lengua", "burla"],
  money_mouth_face: ["dinero", "rico", "plata"],
  hugging_face: ["abrazo", "abrazar", "carino"],
  face_with_hand_over_mouth: ["ups", "vergüenza", "risita"],
  shushing_face: ["silencio", "callar", "shh"],
  thinking_face: ["pensar", "pensativo", "duda", "reflexion"],
  zipper_mouth_face: ["boca cerrada", "secreto", "cremallera"],
  raised_eyebrow: ["ceja", "duda", "escepticismo"],
  neutral_face: ["neutral", "indiferente", "serio"],
  expressionless: ["sin expresion", "aburrido", "serio"],
  no_mouth: ["sin boca", "callado", "mudo"],
  smirk: ["sonrisa picara", "presumido"],
  unamused: ["disgustado", "molesto", "aburrido"],
  roll_eyes: ["ojos en blanco", "fastidio", "molesto"],
  grimacing: ["mueca", "incomodo", "dientes apretados"],
  lying_face: ["mentira", "pinocho", "mentiroso"],
  relieved: ["aliviado", "tranquilo", "feliz"],
  pensive: ["pensativo", "triste", "melancolico"],
  sleepy: ["sueno", "cansado", "dormir"],
  drooling_face: ["babeando", "antojo", "hambre"],
  sleeping: ["dormido", "durmiendo", "zzz"],
  mask: ["mascarilla", "enfermo", "tapaboca"],
  face_with_thermometer: ["fiebre", "termometro", "enfermo"],
  face_with_head_bandage: ["herida", "vendaje", "lastimado"],
  nauseated_face: ["nausea", "asco", "verde"],
  face_vomiting: ["vomito", "asco", "vomitar"],
  sneezing_face: ["estornudo", "resfriado", "pañuelo"],
  hot_face: ["calor", "sudor", "caliente"],
  cold_face: ["frio", "congelado", "helado"],
  woozy_face: ["mareado", "borracho", "confundido"],
  dizzy_face: ["mareo", "aturdido", "rayos"],
  exploding_head: ["explotar", "asombro", "mente volada"],
  cowboy_hat_face: ["vaquero", "sombrero"],
  partying_face: ["fiesta", "celebracion", "cumpleanos"],
  disguised_face: ["disfraz", "incognito", "disimulo"],
  sunglasses: ["lentes de sol", "genial", "cool"],
  nerd_face: ["nerd", "gafas", "estudioso"],
  monocle_face: ["monoculo", "ingles", "elegante"],
  confused: ["confundido", "duda", "perplejo"],
  worried: ["preocupado", "preocupacion"],
  slightly_frowning_face: ["triste leve", "decepcion"],
  open_mouth: ["sorpresa", "boca abierta", "asombro"],
  hushed: ["sorpresa", "asombro", "callado"],
  astonished: ["asombrado", "sorprendido", "shock"],
  flushed: ["sonrojado", "verguenza", "calor"],
  pleading_face: ["suplica", "ojitos", "rogar", "tierno"],
  frowning: ["triste", "frustrado"],
  anguished: ["angustia", "preocupado"],
  fearful: ["miedo", "asustado", "susto"],
  cold_sweat: ["sudor frio", "miedo", "ansiedad"],
  disappointed_relieved: ["alivio", "tristeza"],
  cry: ["llorar", "triste", "lagrima"],
  sob: ["sollozo", "llanto", "muy triste"],
  scream: ["grito", "miedo", "panico"],
  confounded: ["frustrado", "confundido"],
  persevere: ["perseverar", "esforzarse", "agobio"],
  disappointed: ["decepcion", "triste"],
  sweat: ["sudor", "calor"],
  weary: ["agotado", "cansado", "harto"],
  tired_face: ["cansado", "agotado", "exhausto"],
  yawning_face: ["bostezo", "sueno", "aburrido"],
  triumph: ["triunfo", "victoria", "enojo", "humo"],
  rage: ["furia", "enojo", "ira", "rojo"],
  pout: ["enfadado", "enojo"],
  angry: ["enojado", "molesto", "furia"],
  cursing_face: ["maldicion", "groseria", "insulto"],
  smiling_imp: ["diablito", "travieso"],
  imp: ["diablo", "demonio"],
  skull: ["calavera", "craneo", "muerte"],
  skull_and_crossbones: ["veneno", "calavera", "peligro"],
  hankey: ["caca", "popo", "feo"],
  clown_face: ["payaso", "broma"],
  japanese_ogre: ["ogro", "monstruo"],
  japanese_goblin: ["duende", "monstruo"],
  ghost: ["fantasma", "espiritu"],
  alien: ["alien", "extraterrestre"],
  space_invader: ["invasor", "videojuego", "alien"],
  robot: ["robot"],
  smiley_cat: ["gato sonrisa", "feliz"],
  smile_cat: ["gato feliz"],
  joy_cat: ["gato risa", "carcajada"],
  heart_eyes_cat: ["gato amor", "enamorado"],
  smirk_cat: ["gato picaro"],
  kissing_cat: ["gato beso"],
  scream_cat: ["gato susto"],
  crying_cat_face: ["gato triste", "gato llanto"],
  pouting_cat: ["gato enojado"],
  see_no_evil: ["mono", "no ver"],
  hear_no_evil: ["mono", "no oir"],
  speak_no_evil: ["mono", "no hablar"],

  // ── Hearts & love ──
  heart: ["corazon", "amor", "rojo"],
  orange_heart: ["corazon naranja"],
  yellow_heart: ["corazon amarillo"],
  green_heart: ["corazon verde"],
  blue_heart: ["corazon azul"],
  purple_heart: ["corazon morado"],
  black_heart: ["corazon negro"],
  white_heart: ["corazon blanco"],
  brown_heart: ["corazon marron"],
  broken_heart: ["corazon roto", "desamor"],
  two_hearts: ["dos corazones", "amor"],
  revolving_hearts: ["corazones girando"],
  cupid: ["cupido", "flecha", "amor"],
  sparkling_heart: ["corazon brillante"],
  heartpulse: ["latido", "corazon"],
  heartbeat: ["latido", "corazon"],
  gift_heart: ["corazon regalo"],
  heart_decoration: ["corazon decorativo"],

  // ── Gestures / body ──
  "+1": ["pulgar", "bien", "ok", "gusta"],
  thumbsup: ["pulgar arriba", "bien", "ok", "aprobar"],
  "-1": ["pulgar abajo", "mal", "rechazo"],
  thumbsdown: ["pulgar abajo", "mal", "rechazo"],
  ok_hand: ["ok", "perfecto", "bien"],
  pinching_hand: ["pellizco", "pequeno"],
  victory_hand: ["paz", "victoria", "dos"],
  crossed_fingers: ["dedos cruzados", "suerte"],
  love_you_gesture: ["te amo", "rock"],
  metal: ["rock", "metal", "cuernos"],
  call_me_hand: ["llamame", "telefono"],
  point_left: ["izquierda", "senalar"],
  point_right: ["derecha", "senalar"],
  point_up_2: ["arriba"],
  middle_finger: ["dedo medio", "insulto", "groseria"],
  point_down: ["abajo"],
  point_up: ["arriba"],
  wave: ["saludo", "hola", "adios"],
  raised_back_of_hand: ["mano", "parar"],
  raised_hand_with_fingers_splayed: ["mano", "cinco", "parar"],
  hand: ["mano", "alto", "saludo"],
  vulcan_salute: ["vulcano", "star trek"],
  ok_woman: ["ok", "bien"],
  fist_oncoming: ["puno", "golpe"],
  facepunch: ["puno", "golpe"],
  punch: ["puno", "golpe"],
  fist: ["puno", "lucha"],
  fist_left: ["puno izquierda"],
  fist_right: ["puno derecha"],
  raised_hands: ["manos arriba", "celebrar", "alabar"],
  open_hands: ["manos abiertas", "abrazo"],
  palms_up_together: ["manos pidiendo", "rezar"],
  handshake: ["apreton", "saludo", "trato"],
  pray: ["rezar", "orar", "gracias", "por favor"],
  writing_hand: ["escribir", "mano"],
  nail_care: ["unas", "manicura"],
  selfie: ["selfie", "foto"],
  muscle: ["fuerza", "musculo", "biceps"],
  clap: ["aplauso", "aplaudir", "felicitaciones"],

  // ── Animals ──
  dog: ["perro", "cachorro"],
  cat: ["gato", "gatito"],
  mouse: ["raton", "rata"],
  hamster: ["hamster"],
  rabbit: ["conejo"],
  fox_face: ["zorro"],
  bear: ["oso"],
  panda_face: ["panda"],
  koala: ["koala"],
  tiger: ["tigre"],
  lion: ["leon"],
  cow: ["vaca"],
  pig: ["cerdo", "chancho", "puerco"],
  frog: ["rana", "sapo"],
  monkey: ["mono"],
  monkey_face: ["mono"],
  chicken: ["gallina", "pollo"],
  penguin: ["pinguino"],
  bird: ["pajaro", "ave"],
  baby_chick: ["pollito"],
  duck: ["pato"],
  eagle: ["aguila"],
  owl: ["buho", "lechuza"],
  bat: ["murcielago"],
  wolf: ["lobo"],
  boar: ["jabali"],
  horse: ["caballo"],
  unicorn: ["unicornio"],
  bee: ["abeja"],
  bug: ["bicho", "insecto"],
  butterfly: ["mariposa"],
  snail: ["caracol"],
  beetle: ["escarabajo"],
  ant: ["hormiga"],
  spider: ["arana"],
  scorpion: ["escorpion", "alacran"],
  snake: ["serpiente", "vibora"],
  lizard: ["lagartija", "lagarto"],
  turtle: ["tortuga"],
  fish: ["pez", "pescado"],
  whale: ["ballena"],
  dolphin: ["delfin"],
  shark: ["tiburon"],
  octopus: ["pulpo"],
  crab: ["cangrejo"],

  // ── Nature ──
  fire: ["fuego", "llama", "candela"],
  star: ["estrella"],
  star2: ["estrella brillante"],
  sparkles: ["brillos", "destellos", "magia"],
  zap: ["rayo", "energia", "trueno"],
  rainbow: ["arcoiris"],
  sunny: ["sol", "soleado"],
  partly_sunny: ["sol nubes"],
  cloud: ["nube"],
  rain_cloud: ["lluvia"],
  snowflake: ["nieve", "copo"],
  droplet: ["gota", "agua"],
  sweat_drops: ["gotas", "sudor"],
  ocean: ["ola", "mar"],

  // ── Food & drink ──
  pizza: ["pizza"],
  hamburger: ["hamburguesa"],
  fries: ["papas fritas"],
  hotdog: ["pancho", "hot dog", "completo"],
  taco: ["taco"],
  burrito: ["burrito"],
  popcorn: ["palomitas", "cabritas"],
  doughnut: ["dona", "rosquilla"],
  cake: ["pastel", "torta", "cumpleanos"],
  birthday: ["torta cumple", "cumpleanos"],
  cookie: ["galleta"],
  candy: ["caramelo", "dulce"],
  chocolate_bar: ["chocolate"],
  ice_cream: ["helado"],
  apple: ["manzana"],
  green_apple: ["manzana verde"],
  pear: ["pera"],
  orange: ["naranja"],
  lemon: ["limon"],
  banana: ["banana", "platano"],
  watermelon: ["sandia"],
  grapes: ["uvas"],
  strawberry: ["frutilla", "fresa"],
  cherries: ["cerezas"],
  peach: ["durazno", "melocoton"],
  pineapple: ["pina", "ananas"],
  avocado: ["palta", "aguacate"],
  tomato: ["tomate"],
  egg: ["huevo"],
  bread: ["pan"],
  cheese: ["queso"],
  coffee: ["cafe"],
  tea: ["te"],
  beer: ["cerveza"],
  beers: ["cervezas", "brindis"],
  wine_glass: ["copa de vino"],
  cocktail: ["coctel", "trago"],
  champagne: ["champan", "espumante"],

  // ── Travel & places ──
  car: ["auto", "coche", "carro"],
  red_car: ["auto", "coche"],
  taxi: ["taxi"],
  bus: ["bus", "colectivo", "autobus"],
  ambulance: ["ambulancia"],
  police_car: ["patrulla", "policia"],
  fire_engine: ["bombero"],
  truck: ["camion"],
  bike: ["bicicleta", "bici"],
  motor_scooter: ["scooter", "moto"],
  motorcycle: ["motocicleta", "moto"],
  airplane: ["avion"],
  rocket: ["cohete", "lanzar", "exito"],
  ship: ["barco"],
  boat: ["bote"],
  helicopter: ["helicoptero"],
  earth_americas: ["tierra", "mundo", "planeta"],
  earth_africa: ["tierra", "mundo", "planeta"],
  earth_asia: ["tierra", "mundo", "planeta"],
  house: ["casa", "hogar"],
  hospital: ["hospital"],
  school: ["escuela", "colegio"],
  office: ["oficina"],
  bank: ["banco"],

  // ── Activities ──
  soccer: ["futbol", "pelota"],
  basketball: ["basquet", "baloncesto"],
  baseball: ["beisbol"],
  tennis: ["tenis"],
  trophy: ["trofeo", "ganador", "victoria"],
  medal: ["medalla"],
  first_place_medal: ["oro", "primer lugar"],
  second_place_medal: ["plata", "segundo lugar"],
  third_place_medal: ["bronce", "tercer lugar"],
  dart: ["dardo", "objetivo", "meta"],
  bowling: ["bowling", "bolos"],
  video_game: ["videojuego", "gamer"],
  musical_note: ["nota", "musica"],
  notes: ["musica", "notas"],
  microphone: ["microfono", "cantar"],
  headphones: ["audifonos", "auriculares", "musica"],
  guitar: ["guitarra"],

  // ── Objects ──
  iphone: ["telefono", "celular", "movil"],
  computer: ["computadora", "laptop"],
  desktop_computer: ["computadora", "escritorio"],
  printer: ["impresora"],
  keyboard: ["teclado"],
  email: ["correo", "email", "mensaje"],
  envelope: ["sobre", "carta", "correo"],
  inbox_tray: ["bandeja entrada"],
  outbox_tray: ["bandeja salida"],
  package: ["paquete", "caja"],
  mailbox: ["buzon"],
  pencil: ["lapiz"],
  pencil2: ["lapiz"],
  pen: ["lapicera", "boligrafo"],
  memo: ["nota", "apuntes"],
  page_facing_up: ["pagina", "documento"],
  page_with_curl: ["pagina", "documento"],
  bookmark_tabs: ["marcadores"],
  bar_chart: ["grafico", "estadistica"],
  chart_with_upwards_trend: ["grafico subida", "crecimiento", "exito"],
  chart_with_downwards_trend: ["grafico bajada", "caida"],
  clipboard: ["portapapeles", "tablero"],
  briefcase: ["maletin", "trabajo"],
  file_folder: ["carpeta"],
  open_file_folder: ["carpeta abierta"],
  calendar: ["calendario"],
  date: ["calendario", "fecha"],
  bulb: ["bombilla", "idea", "luz"],
  flashlight: ["linterna"],
  battery: ["bateria"],
  electric_plug: ["enchufe"],
  mag: ["lupa", "buscar"],
  mag_right: ["lupa", "buscar"],
  lock: ["candado", "cerrado", "seguridad"],
  unlock: ["candado abierto"],
  key: ["llave"],
  hammer: ["martillo"],
  wrench: ["llave inglesa"],
  gear: ["engranaje", "configuracion"],
  link: ["enlace", "link", "cadena"],
  paperclip: ["clip", "adjuntar"],
  scissors: ["tijeras"],
  bookmark: ["marcador"],
  pushpin: ["chincheta", "fijar", "ubicacion", "pin", "lugar"],
  round_pushpin: ["chincheta", "ubicacion", "pin", "lugar", "mapa"],
  world_map: ["mapa", "ubicacion", "mundo", "lugar", "mapa mundial"],
  triangular_flag_on_post: ["bandera triangular", "marcar"],
  white_flag: ["bandera blanca", "rendicion"],
  black_flag: ["bandera negra"],
  checkered_flag: ["meta", "carrera"],
  bell: ["campana", "notificacion"],
  no_bell: ["silenciar"],
  speech_balloon: ["mensaje", "chat", "globo"],
  thought_balloon: ["pensamiento"],

  // ── Symbols ──
  white_check_mark: ["check", "tic", "ok", "aprobado", "verde"],
  heavy_check_mark: ["check", "tic", "aprobado"],
  ballot_box_with_check: ["check", "casilla"],
  x: ["equis", "x", "rechazado", "error"],
  negative_squared_cross_mark: ["cruz", "error"],
  warning: ["advertencia", "alerta", "atencion"],
  no_entry: ["prohibido", "no pasar"],
  no_entry_sign: ["prohibido"],
  question: ["pregunta", "duda"],
  exclamation: ["exclamacion", "alerta"],
  bangbang: ["doble exclamacion"],
  heart_exclamation: ["corazon exclamacion"],
  hundred: ["cien", "perfecto", "100"],
  "100": ["cien", "perfecto", "100", "puntaje"],
  loudspeaker: ["altavoz", "anuncio"],
  mega: ["megafono", "anuncio"],
  loud_sound: ["sonido fuerte", "altavoz"],
  speaker: ["altavoz"],
  mute: ["silencio", "mudo"],
  zzz: ["dormir", "sueno"],
  arrow_up: ["arriba", "flecha"],
  arrow_down: ["abajo", "flecha"],
  arrow_left: ["izquierda", "flecha"],
  arrow_right: ["derecha", "flecha"],
  arrows_clockwise: ["recargar", "actualizar"],
  arrow_forward: ["reproducir", "play"],
  pause_button: ["pausa"],
  stop_button: ["detener", "stop"],
  no_good: ["no", "rechazar"],
  ok_person: ["ok", "aprobado"],
  raised_hand: ["mano arriba", "alto"],
  tada: ["fiesta", "celebracion", "felicitaciones"],
  confetti_ball: ["confeti", "fiesta", "celebracion"],
  balloon: ["globo", "cumpleanos"],
  gift: ["regalo", "presente"],
  ribbon: ["lazo", "cinta"],
  trophy_2: ["trofeo"],
};

let patched = false;

/**
 * Mutates emoji-mart's data once at module load to append Spanish keywords.
 * Idempotent — safe to call multiple times.
 */
export const patchEmojiDataWithSpanish = (data: EmojiData): EmojiData => {
  if (patched) return data;
  patched = true;
  for (const [id, esKeywords] of Object.entries(KEYWORDS_ES)) {
    const emoji = data.emojis[id];
    if (!emoji) continue;
    const existing = Array.isArray(emoji.keywords) ? emoji.keywords : [];
    const merged = Array.from(new Set([...existing, ...esKeywords]));
    emoji.keywords = merged;
  }
  return data;
};
